"use client";
// Advanced filter rail for the search results. Facets are derived from the
// cars actually returned, so we never offer a filter that matches nothing.
// Filtering happens client-side on purpose: the server picks ONE random unit
// per model, so re-running the query on every checkbox would reshuffle the
// whole list under the visitor.
import { Suspense, useMemo, useState } from "react";
import Link from "next/link";
import { LuChevronDown } from "react-icons/lu";
import SearchBar from "@/components/SearchBar/SearchBar";
import styles from "./search-filters.module.css";

/** Facet groups, in display order. Each pulls its values off a car. */
export const FACETS = [
    // `vehicleType` (SUV/Sedan, from the vehicletypes catalogue) and `category`
    // (City & Highway / Easy Off Road) are different fields — show both.
    { key: "vehicleType", label: "Type", of: (c) => [c?.vehicleType] },
    { key: "category", label: "Category", of: (c) => [c?.category?.translation] },
    { key: "propulsion", label: "Drive", of: (c) => [c?.propulsion] },
    { key: "transmission", label: "Transmission", of: (c) => [c?.transmission] },
    { key: "engine_type", label: "Fuel", of: (c) => [c?.engine_type] },
    { key: "seats", label: "Seats", of: (c) => (c?.passengers ? [`${c.passengers} seats`] : []) },
    { key: "accessoryLabels", label: "Extras", of: (c) => c?.accessoryLabels ?? [] },
    { key: "badges", label: "Features", of: (c) => (c?.badges ?? []).map((b) => b?.name) },
];

const clean = (vals) => vals.map((v) => String(v ?? "").trim()).filter(Boolean);

/** Does a car satisfy every active facet? (values inside a facet are OR'd, facets are AND'd) */
export function carMatches(car, selected, maxPrice) {
    for (const f of FACETS) {
        const picked = selected[f.key];
        if (!picked || !picked.size) continue;
        const have = new Set(clean(f.of(car)));
        let hit = false;
        for (const p of picked) if (have.has(p)) { hit = true; break; }
        if (!hit) return false;
    }
    if (maxPrice != null && perDayOf(car) > maxPrice) return false;
    return true;
}

export function perDayOf(car) {
    const bd = car?.breakdown;
    // Dateless listings (no breakdown) advertise `fromPerDay` on the card, so
    // price the slider off that rather than the delivery-inclusive 1-day total.
    if (!bd && Number(car?.fromPerDay) > 0) return Number(car.fromPerDay);
    const days = Math.max(1, bd?.days ?? car?.dateRange ?? 1);
    return (bd ? bd.rentalNet : car?.currentTotal ?? 0) / days;
}

/** Build {facetKey: [{value, count}]} from the full car list. */
export function buildFacets(cars) {
    const out = {};
    for (const f of FACETS) {
        const counts = new Map();
        for (const c of cars) {
            for (const v of new Set(clean(f.of(c)))) counts.set(v, (counts.get(v) ?? 0) + 1);
        }
        const values = [...counts.entries()]
            .map(([value, count]) => ({ value, count }))
            .sort((a, b) => b.count - a.count || a.value.localeCompare(b.value));
        // Keep a facet only if it can actually narrow the list: several values,
        // or a single value that not every car carries (e.g. only some cars have
        // a Category, so "City & Highway" still filters 44 → 12). Dropping every
        // one-value facet also made Category flicker in and out, since whether
        // the lone "Easy Off Road" car gets drawn changes the value count.
        const partitions = values.length > 1 || (values.length === 1 && values[0].count < cars.length);
        if (partitions) out[f.key] = values;
    }
    return out;
}

function Group({ facet, values, selected, onToggle }) {
    const [open, setOpen] = useState(true);
    const [showAll, setShowAll] = useState(false);
    const shown = showAll ? values : values.slice(0, 6);
    const activeCount = selected?.size ?? 0;

    return (
        <section className={styles.group}>
            <button
                type="button"
                className={styles.groupHead}
                onClick={() => setOpen((o) => !o)}
                aria-expanded={open}
            >
                <span className={styles.groupTitle}>{facet.label}</span>
                {activeCount > 0 && <span className={styles.groupCount}>{activeCount}</span>}
                <span className={`${styles.chev} ${open ? styles.chevOpen : ""}`} aria-hidden>
                    <LuChevronDown />
                </span>
            </button>
            {open && (
                <div className={styles.options}>
                    {shown.map(({ value, count }) => {
                        const on = selected?.has(value) ?? false;
                        return (
                            <label key={value} className={`${styles.opt} ${on ? styles.optOn : ""}`}>
                                <input
                                    type="checkbox"
                                    checked={on}
                                    onChange={() => onToggle(facet.key, value)}
                                />
                                <span className={styles.optLabel}>{value}</span>
                                <span className={styles.optCount}>{count}</span>
                            </label>
                        );
                    })}
                    {values.length > 6 && (
                        <button type="button" className={styles.more} onClick={() => setShowAll((s) => !s)}>
                            {showAll ? "Show less" : `Show all ${values.length}`}
                        </button>
                    )}
                </div>
            )}
        </section>
    );
}

export default function SearchFilters({
    facets,
    selected,
    onToggle,
    onClear,
    priceBounds,
    maxPrice,
    onMaxPrice,
    currency = "€",
    urlData = {},
    filters,
    activeFilters = [],
    enabledFacets,
}) {
    const activeTotal = useMemo(
        () => Object.values(selected).reduce((s, set) => s + (set?.size ?? 0), 0) + (maxPrice < priceBounds.max ? 1 : 0),
        [selected, maxPrice, priceBounds.max],
    );

    // Which filters the admin has left visible (null/undefined = all). A hidden
    // filter is simply never rendered; its (empty) selection never narrows the
    // list. "price" gates the max-price slider, which lives outside FACETS.
    const enabledSet = Array.isArray(enabledFacets) ? new Set(enabledFacets) : null;
    const facetEnabled = (key) => !enabledSet || enabledSet.has(key);
    // Only show the "Filters" header when at least one control renders below it.
    const showPrice = facetEnabled("price") && priceBounds.max > priceBounds.min;
    const hasControls =
        activeFilters.length > 0 || showPrice || FACETS.some((f) => facets[f.key] && facetEnabled(f.key));

    return (
        <aside className={styles.rail} aria-label="Filter results">
            {/* Trip (locations + dates) lives OUTSIDE .scrollArea on purpose: its
                dropdown and calendar pop out, and an overflow:auto ancestor would
                clip them. */}
            <section className={styles.trip}>
                <h2 className={styles.tripTitle}>Your trip</h2>
                <Suspense fallback={null}>
                    <SearchBar compact view={false} urlData={urlData} filters={filters} />
                </Suspense>
            </section>

            <div className={styles.scrollArea}>
                {hasControls && (
                <header className={styles.head}>
                    <h2 className={styles.title}>Filters</h2>
                    {activeTotal > 0 && (
                        <button type="button" className={styles.clear} onClick={onClear}>
                            Clear all
                        </button>
                    )}
                </header>
                )}
                {/* Server-side filters (a landing's subject) — every card shares
                    the value, so buildFacets would hide it; show it explicitly.
                    Unchecking = following the link to the same search without it. */}
                {activeFilters.length > 0 && (
                <section className={styles.group}>
                    <div className={styles.options}>
                        {activeFilters.map(({ key, label, href }) => (
                            <Link key={key} href={href} className={`${styles.opt} ${styles.optOn}`} style={{ textDecoration: "none" }}>
                                <input type="checkbox" checked readOnly tabIndex={-1} aria-hidden />
                                <span className={styles.optLabel}>{label}</span>
                            </Link>
                        ))}
                    </div>
                </section>
                )}
                {showPrice && (
                <section className={styles.group}>
                    <div className={styles.groupHeadStatic}>
                        <span className={styles.groupTitle}>Max price / day</span>
                        <span className={styles.priceVal}>
                            {currency}
                            {Math.round(maxPrice)}
                        </span>
                    </div>
                    <input
                        className={styles.range}
                        type="range"
                        min={Math.ceil(priceBounds.min)}
                        max={Math.ceil(priceBounds.max)}
                        value={maxPrice}
                        onChange={(e) => onMaxPrice(Number(e.target.value))}
                        aria-label="Maximum price per day"
                    />
                    <div className={styles.rangeEnds}>
                        <span>{currency}{Math.ceil(priceBounds.min)}</span>
                        <span>{currency}{Math.ceil(priceBounds.max)}</span>
                    </div>
                    </section>
                )}

                {FACETS.filter((f) => facets[f.key] && facetEnabled(f.key)).map((f) => (
                    <Group
                        key={f.key}
                        facet={f}
                        values={facets[f.key]}
                        selected={selected[f.key]}
                        onToggle={onToggle}
                    />
                ))}
            </div>
        </aside>
    );
}
